/**
 * Which of the three dispatch views is showing, which one opens at sign-in, and which key
 * switches to which.
 *
 * Pure on purpose: it takes a storage and an event target rather than reaching for
 * `localStorage` and `document`, so the rules can be tested without a browser — and so a
 * primitive importing it cannot take the test suite down at module scope.
 */

export const VIEWS = ['desk', 'map', 'timeline'] as const;

export type View = (typeof VIEWS)[number];

/**
 * Cached here; kept on the person's membership.
 *
 * A dispatcher who works the telephone wants the desk; one covering a big rank wants the
 * map; a controller watching for gaps wants the timeline — and they want it at whichever
 * desk they sit at, so the choice is saved on the account (`PreferencesProvider`) and this
 * key is what the shell reads at mount, written from the account at sign-in.
 */
const KEY = 'console.defaultView';

function isView(value: string | null): value is View {
  return VIEWS.includes(value as View);
}

/** The view that opens at sign-in. The desk, until somebody pins another. */
export function defaultView(storage: Pick<Storage, 'getItem'>): View {
  try {
    const stored = storage.getItem(KEY);

    return isView(stored) ? stored : 'desk';
  } catch {
    // A browser with storage disabled still gets a console; it just opens on the desk
    // every morning. Not being able to remember a choice is not a reason to fail.
    return 'desk';
  }
}

export function rememberDefault(storage: Pick<Storage, 'setItem'>, view: View): void {
  try {
    storage.setItem(KEY, view);
  } catch {
    // As above.
  }
}

/** Whether a keystroke landed somewhere that a digit means a digit. */
function typing(target: EventTarget | null): boolean {
  if (target === null || !(typeof target === 'object')) return false;

  const element = target as {
    tagName?: string;
    isContentEditable?: boolean;
    closest?: (selector: string) => unknown;
  };

  if (element.isContentEditable === true) return true;

  if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
    return true;
  }

  // A modal `<dialog>` traps focus, so whatever was pressed was pressed inside it — which
  // is how a pure function can know a dialog is open without asking the document. It is
  // also why every modal in this console is a real `<dialog>` rather than a floating div.
  return element.closest?.('dialog[open]') != null;
}

/**
 * `1`, `2` and `3` switch views — unless the digit was typed into something.
 *
 * Refused inside a field is not a nicety: the common path through this console is typing a
 * house number into a pickup address while somebody reads it out.
 */
export function viewForKey(key: string, target: EventTarget | null): View | null {
  const at = ['1', '2', '3'].indexOf(key);

  if (at === -1 || typing(target)) return null;

  return VIEWS[at];
}
